import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { Concept } from 'src/app/@interfaces/notas.interface';
import { IPurchaseInvoice } from 'src/app/@interfaces/purchaseInvoice.interface';
import { IPurchaseQuery } from 'src/app/@interfaces/purchases.interface';
import { Subject } from 'rxjs';
import { IProvider } from 'src/app/@interfaces/provider.interface';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { DialogComponent } from '../dialog.component';
import { NgxSpinnerService, NgxSpinnerModule } from 'ngx-spinner';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { RefundsService } from './refunds.service';
import { PurchasesService } from 'src/app/@pages/purchases/purchases.service';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { IStorageCompany } from 'src/app/@interfaces/company.interface';
import { TaxValidationsService } from '../../services/validations/tax-validations.service';
import { takeUntil } from 'rxjs/operators';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { CurrencyPipe } from '@angular/common';
import { DianService } from '@shared-services';
import { InventoryArticleWarningComponent } from '@shared-components';

@Component({
  selector: 'app-refunds',
  templateUrl: './refunds.component.html',
  styleUrls: ['./refunds.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatSelectModule,
    MatOptionModule,
    MatButtonModule,
    NgxSpinnerModule,
    CurrencyPipe,
    TranslatePipe,
    InventoryArticleWarningComponent,
  ],
})
export class RefundsComponent implements OnInit {
  @Input() id_invoice: number = 0;
  purchaseInvoice = {} as IPurchaseInvoice;
  dateToday = new Date();
  provider: UntypedFormControl = this.fb.control('', Validators.required);
  article: UntypedFormControl = this.fb.control('', Validators.required);
  refundForm: UntypedFormGroup = this.fb.group({
    itemRows: this.fb.array(
      [
        this.fb.group({
          ids: [],
          warehouse: [],
          article: this.article,
          quantity: [, Validators.required],
          quantity2: [, Validators.required],
          discount: [, Validators.required],
          price: [, Validators.required],
          price2: [, Validators.required],
          subtotal: [0],
          subtotal2: [0],
          subtotal3: [0],
          subtotalNegative: [0],
          quantityNegative: [0],
          priceNegative: [0],
        }),
      ],
      Validators.required,
    ),
    providerCity: [''],
    customerCity: [''],
    tax_incl: false,
    tax: [, Validators.required],
    date: [this.dateToday, Validators.required],
    dueDate: [this.dateToday, Validators.required],
    concept: ['', Validators.required],
  });
  totalCalculated: number = 0;
  totalPurchase: number = 0;
  totalPurchase2: number = 0;

  totalWithholding: number = 0;
  totalWithholding2: number = 0;

  totalIcaTax: number = 0;
  totalIcaTax2: number = 0;

  totalTax: number = 0;
  totalWithoutTax: number = 0;

  get rowsArr() {
    return this.refundForm.get('itemRows') as UntypedFormArray;
  }
  // DIAN response codes for "Nota de ajuste al documento soporte" (sec 16.2.4).
  // Loaded from Api-Dian /api/getDianNoteConcepts/95 in ngOnInit. The form
  // stores `concept.value` (the DIAN code "1"-"5") in refunds.concept, which
  // is later read by `adjustmentNoteElectronicConsecutive` and emitted as
  // <cbc:ResponseCode> on the NAS XML.
  concepts: Concept[] = [];
  purchases: Array<IPurchaseQuery> = [];
  user = '';
  rol = '';
  companyObject = {} as IStorageCompany;
  unsubscribe$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private spinnerService: NgxSpinnerService,
    private dialog: MatDialog,
    private refundsService: RefundsService,
    private purchasesService: PurchasesService,
    private taxValidationsService: TaxValidationsService,
    private cookieAuthService: CookieAuthService,
    private dianService: DianService,
  ) {}

  async ngOnInit() {
    this.spinner();
    this.getAuthValues();
    this.purchaseInvoice = await this.getPurchaseInvoice();
    this.setPurchase(this.purchaseInvoice);
    this.setNoteConcepts();
  }

  // type 95 → "Nota de ajuste al documento soporte" concepts (Resolución DIAN
  // 000167 sec 16.2.4). Endpoint returns [{code, description}]; we map to the
  // {value, viewValue} shape the template iterates so `concept.value` is the
  // DIAN code stored in refunds.concept.
  setNoteConcepts() {
    this.dianService.getDianNoteConcepts(95).subscribe((result) => {
      const rows = (
        result as { data?: { code: string; description: string }[] }
      ).data;
      if (!rows) return;
      this.concepts = rows.map((row) => ({
        value: row.code,
        viewValue: row.description,
      }));
    });
  }

  getPurchaseInvoice() {
    return new Promise<IPurchaseInvoice>((resolve) => {
      this.purchasesService
        .getPurchaseInvoice(this.id_invoice)
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          resolve(result);
        });
    });
  }

  getAuthValues() {
    this.companyObject = this.cookieAuthService.getCompanyObject!;
    this.user = this.cookieAuthService.getUserId!;
    this.rol = this.cookieAuthService.getRolId!;
  }

  displayFnArticle(article: IArticles): string {
    return article ? article.descripcion : '';
  }

  displayFnProvider(provider: IProvider): string {
    return provider ? provider.nombre : '';
  }

  setPurchase(purchaseInvoice: IPurchaseInvoice) {
    const { provider, withholdingTax, icaTax } = purchaseInvoice;
    this.purchases = purchaseInvoice.purchases;
    const [parameter] = this.purchases;
    const { total } = parameter;
    this.totalWithholding = withholdingTax!;
    this.totalIcaTax = icaTax!;
    this.totalPurchase = total;
    this.editPurchase(purchaseInvoice, this.purchases, provider);
  }

  editPurchase(
    purchaseInvoice: IPurchaseInvoice,
    purchases: IPurchaseQuery[],
    provider: IProvider,
  ) {
    const taxBoolean: boolean =
      purchaseInvoice.tax_incl === 'true' ? true : false;
    this.refundForm.patchValue({
      tax: purchaseInvoice.tax,
      tax_incl: taxBoolean,
      providerCity: purchaseInvoice.providerCity,
      customerCity: purchaseInvoice.customerCity,
    });
    this.provider.setValue(provider);
    this.refundForm.setControl('itemRows', this.setPurchases(purchases));
  }

  setPurchases(purchases: IPurchaseQuery[]): UntypedFormArray {
    const formArray = new UntypedFormArray([]);
    for (let index = 0; index < purchases.length; index++) {
      formArray.push(
        this.fb.group({
          ids: purchases[index].id_purchases,
          warehouse: purchases[index].warehouse,
          article: purchases[index].articulo[0],
          quantity: purchases[index].quantity,
          quantity2: purchases[index].quantity,
          discount: purchases[index].discount,
          price: purchases[index].price,
          price2: purchases[index].price,
          subtotal: purchases[index].subtotal,
          subtotal2: purchases[index].subtotal,
          subtotal3: 0,
          subtotalNegative: 0,
          quantityNegative: 0,
          priceNegative: 0,
        }),
      );
    }
    return formArray;
  }

  changeValues() {
    // DIAN code "2" = "Anulación del documento soporte" (total). Zero out
    // every line so the refund reflects a full reversal. All other codes
    // (1=devolución parcial, 3=rebaja/descuento, 4=ajuste precio, 5=otros)
    // are partial — keep the user-entered quantities/prices.
    const concept = this.refundForm.value.concept;
    if (concept === '2') {
      for (let i = 0; i < this.rowsArr.length; i++) {
        this.rowsArr.at(i).get('price')?.setValue(0);
        this.rowsArr.at(i).get('quantity')?.setValue(0);
      }
    }
    this.calculation();
  }

  async disableInputs(
    index: number,
    control: string,
    disableControl: string,
    originalControl: string,
  ) {
    const originalValue =
      this.rowsArr.controls[index].get(originalControl)?.value;
    const currentValue = await this.getInputValue(index, control);
    if (parseFloat(currentValue) !== originalValue) {
      this.rowsArr.controls[index].get(disableControl)?.disable();
    } else {
      this.rowsArr.controls[index].get(disableControl)?.enable();
    }
  }

  getInputValue(index: number, control: string) {
    return new Promise<string>((resolve, reject) => {
      this.rowsArr.controls[index]
        .get(control)
        ?.valueChanges.subscribe((value) => {
          resolve(value);
        });
    });
  }

  create() {
    this.refundsService.createRefund(
      parseInt(this.purchaseInvoice.id_invoice!.toString()),
      this.refundForm,
      this.rowsArr,
      this.totalPurchase2,
      this.totalWithholding2,
      this.totalIcaTax2,
    );
  }

  calculation() {
    //Variables registerForm
    let price;
    let quantity;
    let discount;
    let tax;
    let subtotal;

    //Variables valuesForm
    let previousPrice;
    let previousQuantity;
    let previousSubtotal;
    let calcSubTax;
    let totalTax = 0;
    const contributorID = parseInt(
      this.provider.value.contributorType.id_contributor,
    );

    for (let i = 0; i < this.rowsArr.length; i++) {
      if (this.refundForm.get('tax_incl')?.value === false) {
        if (this.rowsArr.value[i].article.tax[0].value === 0) {
          tax = this.taxValidationsService.getContributorTax(
            contributorID,
            this.refundForm.value.tax,
          );
        } else {
          tax = this.taxValidationsService.getContributorTax(
            contributorID,
            this.rowsArr.value[i].article.tax[0].value,
          );
        }
      } else {
        tax = 0;
      }
      //changing values
      price = this.rowsArr.at(i).get('price')?.getRawValue();
      quantity = this.rowsArr.at(i).get('quantity')?.getRawValue();
      discount = this.rowsArr.value[i].discount;

      //original values
      previousPrice = this.rowsArr.value[i].price2;
      previousQuantity = this.rowsArr.value[i].quantity2;
      previousSubtotal = this.rowsArr.value[i].subtotal2;

      if (tax >= 0) {
        if (discount != 0) {
          subtotal = quantity * price;
          discount = (discount / 100) * subtotal;
          subtotal = subtotal - discount;
          if (this.rowsArr.value[i].article.tax[0].value === 0) {
            tax = (tax / 100) * subtotal;
          } else {
            tax = tax * subtotal;
          }
          //positive price calculation
          this.rowsArr.value[i].priceNegative = price - previousPrice;
          //positive quantity calculation
          this.rowsArr.value[i].quantityNegative = quantity - previousQuantity;
          //subtotal calculation
          this.rowsArr.value[i].subtotal = subtotal + tax;
          //positive subtotal calculation
          calcSubTax = subtotal + tax;
          totalTax = totalTax + tax;
          this.rowsArr.value[i].subtotalNegative =
            calcSubTax - previousSubtotal;

          if (this.rowsArr.value[i].quantityNegative === 0) {
            this.rowsArr.value[i].quantityNegative = previousQuantity;
          }
          if (this.rowsArr.value[i].priceNegative === 0) {
            this.rowsArr.value[i].priceNegative = previousPrice;
          }

          //subtotal without tax
          this.rowsArr.value[i].subtotal3 =
            this.rowsArr.value[i].quantityNegative *
            this.rowsArr.value[i].priceNegative;
        } else {
          subtotal = quantity * price;
          if (this.rowsArr.value[i].article.tax[0].value === 0) {
            tax = (tax / 100) * subtotal;
          } else {
            tax = tax * subtotal;
          }
          //positive price calculation
          this.rowsArr.value[i].priceNegative = price - previousPrice;
          //positive quantity calculation
          this.rowsArr.value[i].quantityNegative = quantity - previousQuantity;
          //subtotal calculation
          this.rowsArr.value[i].subtotal = subtotal + tax;
          //positive subtotal calculation
          calcSubTax = subtotal + tax;
          totalTax = totalTax + tax;
          this.rowsArr.value[i].subtotalNegative =
            calcSubTax - previousSubtotal;

          if (this.rowsArr.value[i].quantityNegative === 0) {
            this.rowsArr.value[i].quantityNegative = previousQuantity;
          }
          if (this.rowsArr.value[i].priceNegative === 0) {
            this.rowsArr.value[i].priceNegative = previousPrice;
          }

          //subtotal without tax
          this.rowsArr.value[i].subtotal3 =
            this.rowsArr.value[i].quantityNegative *
            this.rowsArr.value[i].priceNegative;
        }
      }
    }
    this.totalPurchase2 = 0;
    this.totalCalculated = 0;

    for (let i = 0; i < this.rowsArr.length; i++) {
      //total without tax
      this.totalCalculated =
        this.totalCalculated + this.rowsArr.value[i].subtotal3;
      //negative invoice total
      this.totalPurchase2 =
        this.totalPurchase2 + this.rowsArr.value[i].subtotalNegative;
    }
    this.totalTax = totalTax;
    this.totalWithoutTax = Math.abs(this.totalCalculated);
  }

  closeDialog() {
    this.dialog.closeAll();
  }

  spinner(): void {
    this.spinnerService.show();
    setTimeout(() => {
      this.spinnerService.hide();
    }, 3000);
  }

  openDialogRefundList(purchaseInvoice: IPurchaseInvoice) {
    this.dialog.open(DialogComponent, {
      data: { refundsToFind: purchaseInvoice },
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
